import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { jwtDecode } from 'jwt-decode';
import { environment } from '../../../environments/environment';
import { Role } from '../models/role.enum';
import { LmsAccess, User } from '../models/user.model';
import { AppUrls } from '../routing/app-paths';
import { MicrosoftAuthService } from './microsoft-auth.service';
import { UserService } from './user.service';
import { InternalLearnerService } from '../../features/collaborateur/services/internal-learner.service';
import { LmsActivityHeartbeatService } from '../services/lms-activity-heartbeat.service';
import { DEVICE_ID_KEY, getOrCreateDeviceId, isSessionJwt } from './session.utils';

export interface AuthLoginResponse {
  token: string;
  user: string;
}

export interface AuthLoginBody {
  email: string;
  password: string;
  ecole: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private userService = inject(UserService);
  private microsoftAuth = inject(MicrosoftAuthService);
  private heartbeat = inject(LmsActivityHeartbeatService);
  private internalLearner = inject(InternalLearnerService);

  private readonly publicApiUrl = environment.apiUrl + 'public/';
  private currentUserSignal = signal<User | null>(null);
  private refreshInFlight: Promise<boolean> | null = null;

  readonly currentUser = computed(() => this.currentUserSignal());
  readonly currentRole = computed(() => this.resolveRole(this.currentUserSignal()));
  readonly currentLmsAccesses = computed(() =>
    this.normalizeLmsAccessList(this.currentUserSignal()?.lmsAccess)
  );
  readonly isLoggedIn = computed(() => !!localStorage.getItem('token') && !!this.currentUserSignal());

  constructor() {
    this.userService.currentUser$.subscribe((user) => this.currentUserSignal.set(user));
    this.userService.emitUserFromLocalStorage();

  }

  login(body: AuthLoginBody): Observable<AuthLoginResponse> {
    return this.http.post<AuthLoginResponse>(`${this.publicApiUrl}login`, {
      ...body,
      deviceId: getOrCreateDeviceId()
    });
  }

  /**
   * @route POST /api-v2/public/reset-password-request-lms
   */
  resetPassword(email?: string | null): Observable<unknown> {
    return this.http.post(`${this.publicApiUrl}reset-password-request-lms`, {
      email: email?.trim()?.toLowerCase(),
      school: environment.ecole
    });
  }

  /**
   * @route POST /api-v2/public/check-token-password
   */
  checkTokenPassword(token?: string | null): Observable<unknown> {
    return this.http.post(`${this.publicApiUrl}check-token-password`, {
      token: token?.trim(),
      school: environment.ecole
    });
  }

  /**
   * @route POST /api-v2/public/change-password?token=
   */
  changePassword(
    password?: string | null,
    confirmPassword?: string | null,
    token?: string | null
  ): Observable<unknown> {
    return this.http.post(`${this.publicApiUrl}change-password?token=${token?.trim() ?? ''}`, {
      password,
      confirmPassword
    });
  }

  authWithProvider(oauthToken: string, provider: 'google' | 'microsoft'): Observable<AuthLoginResponse> {
    return this.http.post<AuthLoginResponse>(`${this.publicApiUrl}authWithProvider`, {
      oauthToken,
      provider,
      deviceId: getOrCreateDeviceId()
    });
  }

  loginMicrosoftCollab(oauthToken: string): Observable<AuthLoginResponse> {
    return this.http.post<AuthLoginResponse>(`${this.publicApiUrl}loginMicrosoft`, {
      oauthToken,
      deviceId: getOrCreateDeviceId()
    });
  }

  applySession(response: AuthLoginResponse): void {
    this.userService.applyUserJwt(response.user, response.token);
  }

  finishLoginFlow(): void {
    const user = this.getCurrentUser();
    const lang = user?.langueParDefault || 'fr';
    localStorage.setItem('preferredLanguage', lang);

    const home = this.homePathForCurrentUser();

    const redirectUrl = localStorage.getItem('redirectUrl');
    localStorage.removeItem('redirectUrl');
    localStorage.removeItem('history');

    if (redirectUrl && this.isRedirectAllowedForUser(redirectUrl, home)) {
      void this.router.navigateByUrl(redirectUrl);
      return;
    }

    void this.router.navigateByUrl(home);
  }

  homePathForCurrentUser(): string {
    return this.defaultHomeForUser(this.getCurrentUser());
  }

  getCurrentUser(): User | null {
    return this.userService.getCurrentUserSync();
  }

  startMicrosoftLogin(audience: 'learner' | 'staff' = 'learner'): Promise<void> {
    return this.microsoftAuth.startLogin(audience);
  }

  continueOAuthOnConnecting(
    provider: 'google' | 'microsoft',
    oauthToken: string,
    audience: 'learner' | 'staff' = 'learner'
  ): Promise<boolean> {
    return this.router.navigate(['/connecting'], {
      queryParams: {
        source: provider,
        credential: oauthToken,
        ...(audience === 'staff' ? { audience: 'staff' } : {})
      }
    });
  }

  signInWithMicrosoft(audience: 'learner' | 'staff' = 'learner'): Observable<AuthLoginResponse> {
    return new Observable((subscriber) => {
      void this.microsoftAuth
        .startLogin(audience)
        .then(() => undefined)
        .catch((err: unknown) => subscriber.error(err));
    });
  }

  signInWithGoogle(): Observable<AuthLoginResponse> {
    return new Observable((subscriber) => {
      void this.acquireGoogleCredential()
        .then((credential) => {
          this.authWithProvider(credential, 'google').subscribe({
            next: (res) => {
              subscriber.next(res);
              subscriber.complete();
            },
            error: (err) => subscriber.error(err)
          });
        })
        .catch((err) => subscriber.error(err));
    });
  }

  mountGoogleButton(host: HTMLElement, onCredential: (idToken: string) => void): void {
    const clientId = environment.googleClientId?.trim();
    if (!clientId || !host) {
      return;
    }

    void this.ensureGoogleGisLoaded()
      .then((googleId) => {
        googleId.initialize({
          client_id: clientId,
          callback: (response: { credential?: string }) => {
            if (response?.credential) {
              onCredential(response.credential);
            }
          },
          auto_select: false,
          cancel_on_tap_outside: true,
          ux_mode: 'popup',
          context: 'signin'
        });

        host.replaceChildren();
        googleId.renderButton(host, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          shape: 'rectangular',
          logo_alignment: 'left',
          width: Math.max(host.clientWidth || 0, 240)
        });
      })
      .catch(() => undefined);
  }

  logout(): void {
    this.heartbeat.stop();
    const deviceId = localStorage.getItem(DEVICE_ID_KEY);
    this.http.post(`${this.publicApiUrl}logout`, { deviceId: getOrCreateDeviceId() }).subscribe({
      error: () => undefined
    });
    this.invalidateSession();
    if (deviceId) {
      localStorage.setItem(DEVICE_ID_KEY, deviceId);
    }
    void this.router.navigateByUrl('/connexion');
  }

  invalidateSession(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('userConnected');
    this.userService.clear();
    this.currentUserSignal.set(null);
    this.internalLearner.clear();
  }

  hasRole(roles: Role[]): boolean {
    return this.rolesForCurrentUser().some((role) => roles.includes(role));
  }

  hasLmsAccess(access: LmsAccess): boolean {
    return this.currentLmsAccesses().includes(access);
  }

  /** True when JWT grants at least one LMS staff area via `lmsAccess`. */
  hasAnyLmsAccess(user: User | null = this.currentUserSignal()): boolean {
    return this.normalizeLmsAccessList(user?.lmsAccess).length > 0;
  }

  /**
   * Collaborateur login: `lmsAccess` or a published assigned internal formation grants entry.
   * Returns false when empty — caller must clear session and show an error.
   */
  assertCollaborateurLmsAccess(user: User | null = this.getCurrentUser()): boolean {
    return this.hasAnyLmsAccess(user) || this.internalLearner.hasAccess();
  }

  /** Roles from `lmsAccess` only for staff; learners fall back to IMS profile. */
  rolesForCurrentUser(): Role[] {
    const user = this.currentUserSignal();
    if (!user) {
      return [];
    }
    const accesses = this.normalizeLmsAccessList(user.lmsAccess);
    if (accesses.length) {
      return this.rolesFromLmsAccessList(accesses);
    }
    const role = this.resolveLearnerRole(user);
    return role ? [role] : [];
  }

  homePathForLmsAccess(access: LmsAccess): string {
    switch (access) {
      case 'admin':
        return AppUrls.admin;
      case 'createurContenu':
        return AppUrls.createurContenu;
      case 'formateur':
        return AppUrls.formateur;
      case 'pedagogie':
        return AppUrls.pedagogie;
      case 'vieScolaire':
        return AppUrls.vieScolaire;
      default: {
        const _exhaustive: never = access;
        return _exhaustive;
      }
    }
  }

  isTokenValid(): boolean {
    const token = localStorage.getItem('token');
    if (!token) {
      return false;
    }
    try {
      const payload = jwtDecode<{ exp?: number }>(token);
      if (!payload.exp) {
        return true;
      }
      return payload.exp * 1000 > Date.now();
    } catch {
      return false;
    }
  }

  refreshSession(): Promise<boolean> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }
    const refreshPromise = new Promise<boolean>((resolve) => {
      this.http
        .post<AuthLoginResponse>(`${this.publicApiUrl}refresh`, {
          deviceId: getOrCreateDeviceId()
        })
        .subscribe({
          next: (res) => {
            if (!res?.token || !isSessionJwt(res.token)) {
              resolve(false);
              return;
            }
            this.applySession(res);
            resolve(true);
          },
          error: () => resolve(false)
        });
    }).finally(() => {
      this.refreshInFlight = null;
    });
    this.refreshInFlight = refreshPromise;
    return refreshPromise;
  }

  private defaultHomeForUser(user: User | null): string {
    const fromAccess = this.homePathFromLmsAccess(user?.lmsAccess);
    if (fromAccess) {
      return fromAccess;
    }
    if (this.internalLearner.hasAccess()) {
      return AppUrls.mesFormations;
    }

    const role = this.resolveLearnerRole(user);
    switch (role) {
      case Role.ContentCreator:
        return AppUrls.createurContenu;
      case Role.Admin:
        return AppUrls.admin;
      case Role.Instructor:
        return AppUrls.formateur;
      case Role.Student:
        return AppUrls.etudiant;
      case Role.VieScolaire:
        return AppUrls.vieScolaire;
      case Role.Pedagogie:
        return AppUrls.pedagogie;
      case null:
        return AppUrls.etudiant;
      default: {
        const _exhaustive: never = role;
        return _exhaustive;
      }
    }
  }

  private homePathFromLmsAccess(lmsAccess: unknown): string | null {
    const role = this.resolveRoleFromLmsAccess(lmsAccess);
    if (!role) {
      return null;
    }
    switch (role) {
      case Role.ContentCreator:
        return AppUrls.createurContenu;
      case Role.Instructor:
        return AppUrls.formateur;
      case Role.Admin:
        return AppUrls.admin;
      case Role.Student:
        return AppUrls.etudiant;
      case Role.VieScolaire:
        return AppUrls.vieScolaire;
      case Role.Pedagogie:
        return AppUrls.pedagogie;
      default: {
        const _exhaustive: never = role;
        return _exhaustive;
      }
    }
  }

  private isRedirectAllowedForUser(redirectUrl: string, home: string): boolean {
    const path = redirectUrl.split('?')[0]?.split('#')[0] ?? '';
    if (path === home || path.startsWith(`${home}/`)) {
      return true;
    }
    const allowedHomes = this.allowedHomePathsForCurrentUser();
    return allowedHomes.some((base) => path === base || path.startsWith(`${base}/`));
  }

  private allowedHomePathsForCurrentUser(): string[] {
    const accesses = this.currentLmsAccesses();
    const homes = accesses.length
      ? accesses.map((access) => this.homePathForLmsAccess(access))
      : [this.homePathForCurrentUser()];
    if (this.internalLearner.hasAccess() && !homes.includes(AppUrls.mesFormations)) {
      homes.push(AppUrls.mesFormations);
    }
    return homes;
  }

  private resolveRole(user: User | null): Role | null {
    if (!user) {
      return null;
    }
    // Staff areas: only `lmsAccess` — never role / status / __t.
    const fromLmsAccess = this.resolveRoleFromLmsAccess(user.lmsAccess);
    if (fromLmsAccess) {
      return fromLmsAccess;
    }
    return this.resolveLearnerRole(user);
  }

  /** Learner / formateur IMS profile when `lmsAccess` is absent. */
  private resolveLearnerRole(user: User | null): Role | null {
    if (!user) {
      return null;
    }

    const fromRoleField = this.mapImsRoleField(user.role);
    if (fromRoleField) {
      return fromRoleField;
    }

    const hints = [user.__t, user.type]
      .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
      .map((v) => v.toLowerCase());
    const blob = hints.join(' ');

    if (blob.includes('instructor') || blob.includes('formateur') || blob.includes('nformateur')) {
      return Role.Instructor;
    }
    if (
      blob.includes('student') ||
      blob.includes('apprenant') ||
      blob.includes('etudiant') ||
      blob.includes('élève') ||
      blob.includes('eleve') ||
      blob.includes('prospect') ||
      blob.includes('napprenant')
    ) {
      return Role.Student;
    }

    return Role.Student;
  }

  private resolveRoleFromLmsAccess(lmsAccess: unknown): Role | null {
    const values = this.normalizeLmsAccessList(lmsAccess);
    if (!values.length) {
      return null;
    }
    // Primary role / default home when several flags are present.
    if (values.includes('admin')) {
      return Role.Admin;
    }
    if (values.includes('pedagogie')) {
      return Role.Pedagogie;
    }
    if (values.includes('vieScolaire')) {
      return Role.VieScolaire;
    }
    if (values.includes('createurContenu')) {
      return Role.ContentCreator;
    }
    if (values.includes('formateur')) {
      return Role.Instructor;
    }
    return null;
  }

  private rolesFromLmsAccessList(values: LmsAccess[]): Role[] {
    const roles: Role[] = [];
    for (const access of values) {
      switch (access) {
        case 'admin':
          roles.push(Role.Admin);
          break;
        case 'createurContenu':
          roles.push(Role.ContentCreator);
          break;
        case 'formateur':
          roles.push(Role.Instructor);
          break;
        case 'vieScolaire':
          roles.push(Role.VieScolaire);
          break;
        case 'pedagogie':
          roles.push(Role.Pedagogie);
          break;
        default: {
          const _exhaustive: never = access;
          void _exhaustive;
          break;
        }
      }
    }
    return roles;
  }

  private normalizeLmsAccessList(lmsAccess: unknown): LmsAccess[] {
    const raw = Array.isArray(lmsAccess)
      ? lmsAccess
      : typeof lmsAccess === 'string' && lmsAccess.trim()
        ? [lmsAccess]
        : [];
    const allowed: LmsAccess[] = [
      'createurContenu',
      'formateur',
      'admin',
      'vieScolaire',
      'pedagogie'
    ];
    return raw
      .filter((value): value is string => typeof value === 'string' && !!value.trim())
      .map((value) => value.trim())
      .filter((value): value is LmsAccess => (allowed as string[]).includes(value));
  }

  private mapImsRoleField(role: unknown): Role | null {
    if (typeof role !== 'string' || !role.trim()) {
      return null;
    }
    const normalized = role.trim().toLowerCase().replace(/[\s_]+/g, '-');
    // Collaborateur / admin staff: never from `role` — only `lmsAccess`.
    if (
      normalized === 'user' ||
      normalized === 'collaborateur' ||
      normalized === Role.Admin ||
      normalized === 'administrateur' ||
      normalized === Role.ContentCreator ||
      normalized === 'contentcreator' ||
      normalized === 'createur-contenu' ||
      normalized === 'createurcontenu'
    ) {
      return null;
    }
    switch (normalized) {
      case Role.Instructor:
      case 'formateur':
        return Role.Instructor;
      case Role.Student:
      case 'apprenant':
      case 'etudiant':
      case 'élève':
      case 'eleve':
      case 'prospect':
        return Role.Student;
      default:
        return null;
    }
  }

  private acquireGoogleCredential(): Promise<string> {
    const clientId = environment.googleClientId?.trim();
    if (!clientId) {
      return Promise.reject(new Error('Google Client ID is not configured'));
    }

    return this.ensureGoogleGisLoaded().then(
      (googleId) =>
        new Promise<string>((resolve, reject) => {
          let settled = false;
          const finish = (error?: Error, credential?: string): void => {
            if (settled) {
              return;
            }
            settled = true;
            host.remove();
            if (error) {
              reject(error);
            } else {
              resolve(credential as string);
            }
          };

          googleId.initialize({
            client_id: clientId,
            callback: (response: { credential?: string }) => {
              if (response?.credential) {
                finish(undefined, response.credential);
              } else {
                finish(new Error('Google login did not return a credential'));
              }
            },
            auto_select: false,
            cancel_on_tap_outside: true,
            ux_mode: 'popup',
            context: 'signin'
          });

          const host = document.createElement('div');
          host.setAttribute('aria-hidden', 'true');
          host.style.cssText =
            'position:fixed;left:-9999px;top:0;width:280px;height:44px;overflow:hidden;opacity:0;pointer-events:auto;';
          document.body.appendChild(host);

          googleId.renderButton(host, {
            type: 'standard',
            theme: 'outline',
            size: 'large',
            text: 'continue_with',
            shape: 'rectangular',
            width: 280
          });

          const btn = host.querySelector('div[role="button"]') as HTMLElement | null;
          if (btn) {
            btn.click();
          } else {
            finish(new Error('Google login button failed to render'));
          }

          window.setTimeout(() => {
            finish(new Error('Google login timed out'));
          }, 120_000);
        })
    );
  }

  private ensureGoogleGisLoaded(): Promise<{
    initialize: (config: Record<string, unknown>) => void;
    renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
  }> {
    const w = window as Window & {
      google?: {
        accounts: {
          id: {
            initialize: (config: Record<string, unknown>) => void;
            renderButton: (parent: HTMLElement, options: Record<string, unknown>) => void;
          };
        };
      };
    };

    if (w.google?.accounts?.id?.renderButton) {
      return Promise.resolve(w.google.accounts.id);
    }

    return new Promise((resolve, reject) => {
      const existing = document.getElementById('google-gis') as HTMLScriptElement | null;
      const onReady = (): void => {
        if (w.google?.accounts?.id?.renderButton) {
          resolve(w.google.accounts.id);
        } else {
          reject(new Error('Google Identity Services failed to load'));
        }
      };

      if (existing) {
        existing.addEventListener('load', onReady, { once: true });
        if (w.google?.accounts?.id?.renderButton) {
          onReady();
        }
        return;
      }

      const script = document.createElement('script');
      script.id = 'google-gis';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.onload = onReady;
      script.onerror = () => reject(new Error('Failed to load Google Identity Services'));
      document.head.appendChild(script);
    });
  }
}
